import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Avatar, Sheet, ActionList, ActionRow, SvgBase, PhotoCarousel, PhotoViewerModal } from 'components/ds';
import { IcThumbsUp } from 'components/common/icons/EmojiIcons';
import { VisitPhotoByline } from './VisitPhotos';
import type { VisitPhoto } from './VisitPhotos';
import styles from './VisitPhotoDetail.module.css';

/** 참고 앱과 같은 기준 — 225자를 넘으면 접습니다 */
const CLAMP_LEN = 225;

/**
 * 방문 사진 상세.
 *
 * 사진 확대(PhotoViewerModal)와는 다른 일을 합니다. 저쪽은 사진 한 장을 크게 보는 것이고,
 * 이쪽은 누가 언제 무엇을 남겼는지를 읽는 화면입니다. 좌우로 넘기면 옆 사람 글로 갑니다 —
 * 한 장 보고 닫는 게 아니라 계속 넘겨 보게 하는 게 이 화면의 목적입니다.
 */
export const VisitPhotoDetail = ({
    open, posts, index, onIndexChange, onClose, onHelpful, onEdit, onDelete, onReport, onBlock,
}: {
    open: boolean;
    posts: VisitPhoto[];
    index: number;
    onIndexChange: (index: number) => void;
    onClose: () => void;
    onHelpful?: (post: VisitPhoto) => void;
    onEdit?: (post: VisitPhoto) => void;
    onDelete?: (post: VisitPhoto) => void;
    onReport?: (post: VisitPhoto) => void;
    onBlock?: (post: VisitPhoto) => void;
}) => {
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);
    const [menu, setMenu] = useState(false);
    /** 사진만 크게 보기로 연 장수. null 이면 닫힘 */
    const [viewer, setViewer] = useState<number | null>(null);

    const post = posts[index];

    // 다른 글로 넘어가면 접은 상태로 되돌립니다
    useEffect(() => { setExpanded(false); }, [index]);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            if (e.key === 'ArrowLeft' && index > 0) onIndexChange(index - 1);
            if (e.key === 'ArrowRight' && index < posts.length - 1) onIndexChange(index + 1);
        };
        window.addEventListener('keydown', onKey);
        return () => {
            document.body.style.overflow = prev;
            window.removeEventListener('keydown', onKey);
        };
    }, [open, index, posts.length, onClose, onIndexChange]);

    if (!open || !post) return null;

    const content = post.content || '';
    const long = content.length > CLAMP_LEN;
    const shown = expanded || !long ? content : `${content.slice(0, CLAMP_LEN)}…`;
    const host = document.getElementById('portal') || document.body;

    const node = (
        <div className={styles.overlay} role="dialog" aria-modal="true">
            <header className={styles.bar}>
                <button type="button" className={styles.iconBtn} onClick={onClose} aria-label={t('common.close')}>
                    <SvgBase size={20} strokeWidth={1.8}><path d="M6 6l12 12M18 6L6 18" /></SvgBase>
                </button>
                <span className={styles.pager}>{index + 1} / {posts.length}</span>
                <button type="button" className={styles.iconBtn} onClick={() => setMenu(true)} aria-label={t('common.more')}>
                    <SvgBase size={20} strokeWidth={2}>
                        <circle cx="5" cy="12" r="1" /><circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" />
                    </SvgBase>
                </button>
            </header>

            {/* 별점만 남긴 후기는 사진이 없습니다. 빈 검은 칸을 두지 않고 글만 보여줍니다 */}
            <div className={post.photos.length > 0 ? styles.stage : styles.stageEmpty}>
                {/* 사진을 누르면 그 사진만 크게 — 상세는 읽는 화면이고
                    사진을 크게 보는 것은 다른 일입니다 */}
                <PhotoCarousel
                    photos={post.photos}
                    className={styles.carousel}
                    onSelect={setViewer}
                />
                {index > 0 && (
                    <button
                        type="button"
                        className={`${styles.step} ${styles.stepPrev}`}
                        onClick={() => onIndexChange(index - 1)}
                        aria-label={t('visitPhoto.prevPost')}
                    >
                        <SvgBase size={22}><path d="M15 5l-7 7 7 7" /></SvgBase>
                    </button>
                )}
                {index < posts.length - 1 && (
                    <button
                        type="button"
                        className={`${styles.step} ${styles.stepNext}`}
                        onClick={() => onIndexChange(index + 1)}
                        aria-label={t('visitPhoto.nextPost')}
                    >
                        <SvgBase size={22}><path d="M9 5l7 7-7 7" /></SvgBase>
                    </button>
                )}
            </div>

            <div className={styles.body}>
                <div className={styles.who}>
                    <Avatar
                        size="sm"
                        person={{ userNo: post.userNo, name: post.nickname, imageUrl: post.profileUrl }}
                    />
                    <div className={styles.whoText}>
                        <span className={styles.nick}>{post.nickname}</span>
                        <VisitPhotoByline post={post} />
                    </div>
                </div>

                {content && (
                    <p className={styles.content}>
                        {shown}
                        {long && (
                            <button type="button" className={styles.more} onClick={() => setExpanded(v => !v)}>
                                {expanded ? t('common.collapse') : t('common.more')}
                            </button>
                        )}
                    </p>
                )}

                {post.tags && post.tags.length > 0 && (
                    <p className={styles.tags}>
                        {post.tags.map(tag => <span key={tag} className={styles.tag}>#{tag}</span>)}
                    </p>
                )}

                <button
                    type="button"
                    className={post.helpful ? `${styles.helpful} ${styles.helpfulOn}` : styles.helpful}
                    aria-pressed={!!post.helpful}
                    onClick={() => onHelpful?.(post)}
                >
                    <IcThumbsUp size={14} />
                    <span>{t('placeReview.helpful')}</span>
                    {post.helpfulCount ? <span className={styles.hCount}>{post.helpfulCount}</span> : null}
                </button>
            </div>

            {/* 더보기 — 내 글이면 수정·삭제, 남의 글이면 신고·차단 */}
            <Sheet open={menu} onClose={() => setMenu(false)} title={t('common.more')} closeLabel={t('common.close')}>
                <ActionList>
                    {post.mine ? (
                        <>
                            <ActionRow title={t('common.edit')} noArrow onClick={() => { setMenu(false); onEdit?.(post); }} />
                            <ActionRow title={t('common.delete')} noArrow danger onClick={() => { setMenu(false); onDelete?.(post); }} />
                        </>
                    ) : (
                        <>
                            <ActionRow title={t('visitPhoto.report')} noArrow onClick={() => { setMenu(false); onReport?.(post); }} />
                            <ActionRow title={t('visitPhoto.block')} noArrow danger onClick={() => { setMenu(false); onBlock?.(post); }} />
                        </>
                    )}
                </ActionList>
            </Sheet>

            {/* 크게 보기 — 좌우로 넘기고 공유할 수 있습니다 */}
            <PhotoViewerModal
                open={viewer !== null}
                photos={post.photos}
                index={viewer ?? 0}
                alt={post.nickname || ''}
                shareTitle={post.nickname || ''}
                onClose={() => setViewer(null)}
            />
        </div>
    );

    return createPortal(node, host);
};
